import React, { useState, forwardRef, useImperativeHandle } from 'react';
import {
  Menu,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  Divider,
  ListItemButton,
  IconButton,
  Stack,
} from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { Close } from '@mui/icons-material';
import { FormattedMessage } from 'react-intl';

function MenuItemWithIcon({ item }) {
  return (
    <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 0.5 }}>
      <item.Icon />
      <Typography color="primary" variant="subtitle2" component="div">
        {item.name}
      </Typography>
    </Stack>
  );
}

const MainMenu = forwardRef(({ items }, ref) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const visibleItems = items.filter(item => item.hasPermission);
  const columns = Math.max(1, visibleItems.length);
  const menuWidth = Math.min(columns * 220, 720);

  useImperativeHandle(ref, () => ({
    openMenu: anchor => setAnchorEl(anchor),
    closeMenu: () => setAnchorEl(null),
  }));

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      slotProps={{
        paper: {
          sx: {
            maxHeight: '70vh',
            p: 0,
            overflow: 'hidden',
          },
        },
      }}>
      <Box
        sx={{
          width: menuWidth,
          p: 1,
          overflowY: 'auto',
          maxHeight: '70vh',
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ flexGrow: 1 }}>
            <FormattedMessage
              id="APPLICATION_MENU"
              defaultMessage="Application Menu"
            />
          </Typography>
          <IconButton
            aria-label="Close setup menu"
            size="small"
            onClick={handleClose}
            sx={{ ml: 1 }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>
        <Divider />

        <Grid2 container spacing={2} sx={{ mt: 1 }}>
          {visibleItems.map(item => (
            <Grid2 key={item.name} xs={12} sm={12 / columns} md={12 / columns}>
              <MenuItemWithIcon item={item} />
              <Divider color="primary" />
              <Box>
                {item.children && item.children.length > 0 && (
                  <List dense>
                    {item.children.map(child => {
                      if (!child.hasPermission) {
                        return null;
                      }
                      return (
                        <ListItem dense disablePadding key={child.name}>
                          <ListItemButton
                            alignItems="flex-start"
                            disableGutters
                            dense
                            sx={{ p: 0.3 }}
                            onClick={() => {
                              child.onClick();
                              handleClose();
                            }}>
                            <ListItemText primary={child.name} />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Box>
            </Grid2>
          ))}
        </Grid2>
      </Box>
    </Menu>
  );
});

export default MainMenu;
